import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasAdminManagerRole } from '../utils/roles';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { adminAPI, manifestReceivingAPI } from '../services/api';
import { formatDate } from '../utils/formatters';
import VoidReceiptModal from './modals/VoidReceiptModal';
import LoadingSpinner from './LoadingSpinner';

// Admin-only receiving history for an inbound request: every receive event
// (receipt) with what it brought in, and a Void action that reverses the
// receipt via the inventory corrections API. ASN requests list
// ReceivingSessions; manifest-linked requests list InboundProjectReceipts.
const statusBadge = (status) => {
  const styles = {
    completed: 'bg-green-100 text-green-800',
    voided: 'bg-red-100 text-red-800',
    cancelled: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800'
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status === 'cancelled' ? 'voided' : status}
    </span>
  );
};

const ReceivingHistoryPanel = ({ requestId, requestType, manifestId, onVoided }) => {
  // Receipt voids are role-gated on the backend (RF15: admin/manager).
  const { user } = useAuth();
  const canVoid = hasAdminManagerRole(user);
  const queryClient = useQueryClient();
  const [voidTarget, setVoidTarget] = useState(null);

  const isManifest = requestType === 'inbound_manifest';

  const sessionsQuery = useQuery(
    ['receiving-sessions', requestId],
    () => adminAPI.getRequestReceivingSessions(requestId),
    { enabled: !isManifest && !!requestId, refetchOnWindowFocus: false }
  );

  const manifestHistoryQuery = useQuery(
    ['manifest-receiving-history', manifestId],
    () => manifestReceivingAPI.getManifestReceivingHistory(manifestId),
    { enabled: isManifest && !!manifestId, refetchOnWindowFocus: false }
  );

  const voidMutation = useMutation({
    mutationFn: (payload) => adminAPI.voidReceipt(payload),
    onSuccess: (result) => {
      const review = result?.correction?.requiresBillingReview;
      toast.success(
        `${result?.message || 'Receipt voided.'}${review ? ' Flagged for billing review.' : ''}`
      );
      setVoidTarget(null);
      queryClient.invalidateQueries(['receiving-sessions', requestId]);
      queryClient.invalidateQueries(['manifest-receiving-history', manifestId]);
      // The corrections ledger has a new document -- refresh any cached list.
      queryClient.invalidateQueries(['admin-corrections']);
      if (onVoided) onVoided();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to void receipt');
    }
  });

  const handleConfirmVoid = ({ reasonCode, customerReason, internalNotes }) => {
    voidMutation.mutate({
      receiptType: voidTarget.receiptType,
      receiptId: voidTarget.id,
      reasonCode,
      customerReason,
      internalNotes
    });
  };

  const activeQuery = isManifest ? manifestHistoryQuery : sessionsQuery;
  const isLoading = activeQuery.isLoading;
  const isError = activeQuery.isError;

  // Normalize both receipt systems into one display shape. Void
  // eligibility comes from the backend (it mirrors the void service's
  // rules); anything not explicitly voidable shows its reason instead.
  let receipts = [];
  if (isManifest) {
    const history = manifestHistoryQuery.data?.data || [];
    receipts = (Array.isArray(history) ? history : []).map((receipt) => ({
      id: receipt.id,
      receiptType: 'manifest_receipt',
      receiptNumber: receipt.receipt_number,
      receivedAt: receipt.received_at || receipt.created_at,
      receivedBy: receipt.ReceivedBy?.name,
      status: receipt.status,
      voidable: receipt.voidable === true,
      ineligibleReason: receipt.ineligible_reason,
      lines: (receipt.InboundProjectReceiptLines || []).map((line) => ({
        id: line.id,
        sku: line.InventoryItem?.product_sku,
        name: line.InventoryItem?.product_name,
        quantity: line.quantity_received
      }))
    }));
  } else {
    receipts = (sessionsQuery.data?.data || []).map((session) => ({
      id: session.id,
      receiptType: 'receiving_session',
      receiptNumber: session.receipt_number,
      receivedAt: session.received_at,
      receivedBy: session.ReceivedBy?.name,
      status: session.status,
      voidable: session.voidable === true,
      ineligibleReason: session.ineligible_reason,
      lines: (session.Lines || []).map((line) => ({
        id: line.id,
        sku: line.InventoryItem?.product_sku,
        name: line.InventoryItem?.product_name,
        quantity: line.quantity_received,
        action: line.action
      }))
    }));
  }

  const voidTargetUnits = voidTarget
    ? voidTarget.lines.reduce((sum, line) => sum + (line.quantity || 0), 0)
    : 0;

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Receiving History</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Each receipt records one receive event. Voiding a receipt reverses its
          inventory, stock, and billing while preserving history.
        </p>
      </div>
      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="p-6"><LoadingSpinner /></div>
        ) : isError ? (
          <div className="px-4 py-6 text-sm">
            <p className="text-red-600 mb-2">Failed to load receiving history.</p>
            <button
              onClick={() => activeQuery.refetch()}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Retry
            </button>
          </div>
        ) : receipts.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500">No receipts recorded yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {receipts.map((receipt) => (
              <li key={receipt.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{receipt.receiptNumber}</span>
                      {statusBadge(receipt.status)}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(receipt.receivedAt)}
                      {receipt.receivedBy ? ` · received by ${receipt.receivedBy}` : ''}
                    </p>
                  </div>
                  {receipt.voidable && canVoid ? (
                    <button
                      onClick={() => setVoidTarget(receipt)}
                      className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                    >
                      Void Receipt
                    </button>
                  ) : receipt.ineligibleReason && receipt.status !== 'voided' && receipt.status !== 'cancelled' ? (
                    <span className="text-xs text-gray-400 italic max-w-xs text-right">
                      {receipt.ineligibleReason}
                    </span>
                  ) : null}
                </div>
                {receipt.lines.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {receipt.lines.map((line) => (
                      <div key={line.id} className="flex justify-between py-0.5">
                        <span>
                          {line.sku || 'Unknown SKU'}
                          {line.name ? ` — ${line.name}` : ''}
                          {line.action && line.action !== 'received' ? ` (${line.action})` : ''}
                        </span>
                        <span className="font-medium">{line.quantity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Keyed by receipt so each void starts with fresh reason/notes state --
          a reused mounted modal would leak one receipt's explanation into
          another correction. */}
      <VoidReceiptModal
        key={voidTarget?.id || 'no-target'}
        isOpen={!!voidTarget}
        onClose={() => setVoidTarget(null)}
        receiptLabel={voidTarget ? `receipt ${voidTarget.receiptNumber}` : ''}
        unitCount={voidTargetUnits}
        onConfirm={handleConfirmVoid}
        isLoading={voidMutation.isLoading}
      />
    </div>
  );
};

export default ReceivingHistoryPanel;
